import { jwtDecode } from 'jwt-decode';
import { AUTH_ROLES, TOKEN_KEYS, LOGIN_ROUTES } from '../utils/authConstants';
import { apiRequest } from './apiClient';
import { getApiUrl } from '../config/api';

// Cache bust: 2024-02-24 19:30

class AuthServiceV2 {
  // Get token key for a specific role
  static getTokenKey(role, tokenType = 'access') {
    const roleKey = role.toUpperCase();
    const tokenSuffix = tokenType === 'access' ? 'ACCESS_TOKEN' : 'REFRESH_TOKEN';
    return TOKEN_KEYS[`${roleKey}_${tokenSuffix}`];
  }

  // Store tokens for a specific role
  static storeTokens(role, accessToken, refreshToken) {
    localStorage.setItem(this.getTokenKey(role, 'access'), accessToken);
    if (refreshToken) {
      localStorage.setItem(this.getTokenKey(role, 'refresh'), refreshToken);
    }
    localStorage.setItem(TOKEN_KEYS.AUTH_ROLE, role);
  }

  // Get token for a specific role
  static getToken(role, tokenType = 'access') {
    return localStorage.getItem(this.getTokenKey(role, tokenType));
  }

  // Clear tokens for a specific role
  static clearTokens(role) {
    localStorage.removeItem(this.getTokenKey(role, 'access'));
    localStorage.removeItem(this.getTokenKey(role, 'refresh'));
    
    // Clear auth role if no tokens remain
    const hasAnyToken = Object.values(AUTH_ROLES).some(r => this.getToken(r));
    if (!hasAnyToken) {
      localStorage.removeItem(TOKEN_KEYS.AUTH_ROLE);
    }
  }

  // Get current auth role
  static getCurrentRole() {
    return localStorage.getItem(TOKEN_KEYS.AUTH_ROLE);
  }

  // Admin login
  static async adminLogin(credentials) {
    try {
      const result = await apiRequest({
        endpoint: '/admin/api/auth/admin/login',
        method: 'POST',
        auth: false,
        body: credentials,
        showSuccess: true,
        successMessage: 'Login successful'
      });

      if (result.success) {
        // Store tokens for admin role
        this.storeTokens(AUTH_ROLES.ADMIN, result.data.access_token, result.data.refresh_token);
        
        // Return basic user data
        const userData = {
          email: credentials.email,
          username: credentials.email.split('@')[0],
          name: 'Admin User',
          phone: '',
          avatar: '',
          address: '',
          role: AUTH_ROLES.ADMIN,
          status: true
        };
        
        return {
          success: true,
          data: userData
        };
      } else {
        return {
          success: false,
          message: result.message || 'Login failed'
        };
      }
    } catch (error) {
      console.error('Admin login error:', error);
      return {
        success: false,
        message: 'Network error. Please check your connection.'
      };
    }
  }

  // User login
  static async userLogin(credentials) {
    try {
      const result = await apiRequest({
        endpoint: '/admin/api/auth/user/login',
        method: 'POST',
        auth: false,
        body: credentials,
        showSuccess: true,
        successMessage: 'Login successful'
      });

      if (result.success) {
        // Store tokens for user role
        this.storeTokens(AUTH_ROLES.USER, result.data.access_token, result.data.refresh_token);
        
        // Return user data from token
        const userData = this.getCurrentUser(AUTH_ROLES.USER);
        
        return {
          success: true,
          data: userData
        };
      }
      
      return result;
    } catch (error) {
      console.error('User login error:', error);
      return {
        success: false,
        message: 'Login failed'
      };
    }
  }

  // Refresh token for a specific role
  static async refreshToken(role) {
    try {
      const refreshToken = this.getToken(role, 'refresh');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      let endpoint;
      if (role === AUTH_ROLES.ADMIN) {
        endpoint = '/admin/api/auth/refresh';
      } else if (role === AUTH_ROLES.USER) {
        endpoint = '/admin/api/auth/refresh/user';
      } else {
        throw new Error('Refresh not implemented for this role');
      }

      const response = await fetch(this.getApiUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: refreshToken })
      });

      const result = await response.json();
      
      if (result.status) {
        this.storeTokens(role, result.data.access_token, result.data.refresh_token);
        return result.data.access_token;
      }
      
      throw new Error('Token refresh failed');
    } catch (error) {
      console.error('Token refresh error:', error);
      this.logout(role);
      throw error;
    }
  }

  // Logout for a specific role
  static async logout(role) {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  // Check if user is authenticated for a specific role
  static isAuthenticated(role = null) {
    const targetRole = role || this.getCurrentRole();
    if (!targetRole) return false;
    
    const token = this.getToken(targetRole);
    if (!token) return false;
    
    try {
      // Basic JWT format check
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid JWT format: token should have 3 parts');
        return false;
      }
      
      // Check if token is expired
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      console.error('Token validation error:', error);
      // Remove invalid token
      this.logout(targetRole);
      return false;
    }
  }

  // Get current user data for a specific role
  static getCurrentUser(role = null) {
    const targetRole = role || this.getCurrentRole();
    if (!targetRole) return null;
    
    const token = this.getToken(targetRole);
    if (!token) return null;
    
    try {
      // Basic JWT format check
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid JWT format: token should have 3 parts');
        this.logout(targetRole);
        return null;
      }
      
      // Check if token is expired first
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Date.now() / 1000;
      
      if (payload.exp <= currentTime) {
        console.log('Token expired, removing from storage');
        this.clearTokens(targetRole);
        return null;
      }
      
      return {
        email: payload.email || `${targetRole}@example.com`,
        username: payload.username || payload.email?.split('@')[0] || targetRole,
        name: payload.name || `${targetRole.charAt(0).toUpperCase() + targetRole.slice(1)} User`,
        phone: payload.phone || '',
        avatar: payload.avatar || '',
        address: payload.address || '',
        role: payload.user_role || payload.role || targetRole,
        status: payload.status !== undefined ? payload.status : true
      };
    } catch (error) {
      console.error('Error parsing user token:', error);
      // Remove invalid token
      this.logout(targetRole);
      return null;
    }
  }

  // Get correct login route for a role
  static getLoginRoute(role) {
    return LOGIN_ROUTES[role] || LOGIN_ROUTES[AUTH_ROLES.USER];
  }

  // Helper to get API URL (move from apiClient if needed)
  static getApiUrl(endpoint) {
    return getApiUrl(endpoint);
  }

  // Check if token is valid
  static isTokenValid(token, role) {
    // Force refresh - cache bust
    console.log('🔍 Checking token validity for role:', role);
    
    if (!token) {
      return false;
    }
    
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      // Check if token is expired
      if (decoded.exp && decoded.exp < currentTime) {
        console.log('Token expired:', decoded.exp, 'current:', currentTime);
        return false;
      }
      
      // Additional validation based on role if needed
      if (role === AUTH_ROLES.ADMIN) {
        // Admin-specific validation
        return decoded.user_id && decoded.email;
      } else if (role === AUTH_ROLES.USER) {
        // User-specific validation
        return decoded.user_id && decoded.email;
      }
      
      return decoded.user_id && decoded.email;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }
}

export default AuthServiceV2;
